/**
 * Corner-radius helpers (Agent Air, masking rework #4) — pure functions that turn
 * a cutout's bounding box + a normalized corner radius into a rounded-rectangle
 * polygon. radius 0 → the plain 4-corner bbox; radius 0.5 (the max, = half the
 * shorter side) → a circle/ellipse inscribed in the bbox. Each rounded corner is
 * sampled into a few arc points. Coordinates are normalized 0..1.
 *
 * The submitted/rendered geometry is just the resulting polygon — `cornerRadius`
 * is stored on the Mask as editor metadata so repeated presses recompute from
 * the (preserved) bounding box rather than compounding rounding errors.
 */
import type { Point, Polygon } from "@genz/engine-bind";

/** Axis-aligned bounding box in normalized 0..1 coordinates. */
export interface BBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Largest normalized corner radius (half the shorter side → circle/ellipse). */
export const MAX_CORNER_RADIUS = 0.5;

/** Number of sampled points per rounded corner arc (~6–8 keeps it smooth). */
const ARC_SAMPLES = 7;

/** Axis-aligned bounding box of a polygon (normalized coordinates). */
export function boundingBox(poly: Polygon): BBox {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of poly) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

/**
 * Generate a rounded-rectangle polygon for `bbox`. `radius` is normalized 0..0.5
 * of the shorter side: 0 = sharp bbox rectangle, 0.5 = full circle/ellipse.
 * Corners are sampled into `ARC_SAMPLES` points each; the polygon is closed in
 * the sense that the first and last points are distinct vertices around the
 * loop (no duplicated closing vertex), matching the rest of the editor.
 */
export function roundedRectPolygon(bbox: BBox, radius: number): Polygon {
  const minX = Math.min(bbox.minX, bbox.maxX);
  const maxX = Math.max(bbox.minX, bbox.maxX);
  const minY = Math.min(bbox.minY, bbox.maxY);
  const maxY = Math.max(bbox.minY, bbox.maxY);
  const w = maxX - minX;
  const h = maxY - minY;

  const t = clamp(radius, 0, MAX_CORNER_RADIUS) / MAX_CORNER_RADIUS; // 0..1 fraction of half-side
  // Per-axis arc radius: at t=1 each is half its side (so a square → circle, a
  // rectangle → ellipse). Degenerate (zero-size) boxes get a plain rectangle.
  const rx = (w / 2) * t;
  const ry = (h / 2) * t;

  if (rx <= 1e-9 || ry <= 1e-9) {
    // Sharp rectangle (radius 0 or degenerate box) — 4 bbox corners.
    return [
      { x: minX, y: minY },
      { x: maxX, y: minY },
      { x: maxX, y: maxY },
      { x: minX, y: maxY },
    ];
  }

  // Centers of the four corner arcs (inset by the per-axis radius).
  const corners = [
    { cx: maxX - rx, cy: minY + ry, a0: -Math.PI / 2, a1: 0 }, // top-right
    { cx: maxX - rx, cy: maxY - ry, a0: 0, a1: Math.PI / 2 }, // bottom-right
    { cx: minX + rx, cy: maxY - ry, a0: Math.PI / 2, a1: Math.PI }, // bottom-left
    { cx: minX + rx, cy: minY + ry, a0: Math.PI, a1: (3 * Math.PI) / 2 }, // top-left
  ];

  const poly: Polygon = [];
  for (const c of corners) {
    for (let i = 0; i < ARC_SAMPLES; i++) {
      const a = c.a0 + ((c.a1 - c.a0) * i) / (ARC_SAMPLES - 1);
      poly.push({
        x: clamp01(c.cx + Math.cos(a) * rx),
        y: clamp01(c.cy + Math.sin(a) * ry),
      });
    }
  }
  return dedupeAdjacent(poly);
}

/** Step a cutout's corner radius by `delta`, clamped to [0, 0.5]. */
export function stepRadius(current: number | undefined, delta: number): number {
  return clamp(round3((current ?? 0) + delta), 0, MAX_CORNER_RADIUS);
}

/** Drop consecutive duplicate points (arc joints can coincide at low radius). */
function dedupeAdjacent(poly: Polygon): Polygon {
  const out: Polygon = [];
  for (const p of poly) {
    const prev = out[out.length - 1];
    if (!prev || Math.abs(prev.x - p.x) > 1e-7 || Math.abs(prev.y - p.y) > 1e-7) out.push(p);
  }
  // Also collapse a wrap-around duplicate (last == first).
  if (out.length > 1) {
    const a = out[0];
    const b = out[out.length - 1];
    if (Math.abs(a.x - b.x) <= 1e-7 && Math.abs(a.y - b.y) <= 1e-7) out.pop();
  }
  return out;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
function clamp01(v: number): number {
  return clamp(v, 0, 1);
}
function round3(v: number): number {
  return Math.round(v * 1000) / 1000;
}

/** Squared distance from `p` to the segment a→b (normalized space). */
function distSqToSegment(p: Point, a: Point, b: Point): number {
  const vx = b.x - a.x, vy = b.y - a.y;
  const len2 = vx * vx + vy * vy;
  // A degenerate (zero-length) edge collapses to its endpoint.
  const t = len2 > 0 ? clamp(((p.x - a.x) * vx + (p.y - a.y) * vy) / len2, 0, 1) : 0;
  const dx = p.x - (a.x + t * vx), dy = p.y - (a.y + t * vy);
  return dx * dx + dy * dy;
}

/**
 * Insert `pt` into the CLOSED polygon on the edge it is nearest to, returning a NEW polygon.
 *
 * Alt-clicking used to APPEND the point to the end of the print-area array. For anything but a
 * click near the last edge that produces a self-intersecting polygon — which `geometryValidate`
 * then rejects, so Save was blocked by a vertex the operator thought they were just adding
 * (audit ED-13). Inserting on the nearest edge keeps the ring simple. Polygons with fewer than
 * two points have no edge to split, so the point is appended.
 */
export function insertOnNearestEdge(poly: Polygon, pt: Point): Polygon {
  if (poly.length < 2) return [...poly, pt];
  let bestEdge = 0;
  let bestDist = Infinity;
  for (let i = 0; i < poly.length; i++) {
    const d = distSqToSegment(pt, poly[i], poly[(i + 1) % poly.length]);
    if (d < bestDist) { bestDist = d; bestEdge = i; }
  }
  const out = poly.slice();
  out.splice(bestEdge + 1, 0, pt); // between the edge's two endpoints
  return out;
}

/**
 * Chaikin corner-cutting: smooth a closed polygon into a curve-like polygon
 * (each iteration replaces every vertex with two points 1/4 and 3/4 along its
 * edges). 2 iterations reads as a smooth rounded shape while staying a plain
 * polygon the render engine warps as-is. Polygons with <3 points are returned
 * unchanged. Coordinates stay in the input's space (normalized 0..1).
 */
export function chaikin(poly: Polygon, iterations = 2): Polygon {
  let pts = poly;
  for (let it = 0; it < iterations && pts.length >= 3; it++) {
    const next: Polygon = [];
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i];
      const b = pts[(i + 1) % pts.length];
      next.push({ x: a.x * 0.75 + b.x * 0.25, y: a.y * 0.75 + b.y * 0.25 });
      next.push({ x: a.x * 0.25 + b.x * 0.75, y: a.y * 0.25 + b.y * 0.75 });
    }
    pts = next;
  }
  return pts;
}
